import '@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css';
import 'mapbox-gl/dist/mapbox-gl.css';

import { Button, Flex, Icon } from '@chakra-ui/react';
import { ListingComparableSetFragment } from '@client/graphql/__generated__/types';
import { debounce } from 'lodash';
import { StarIcon } from 'lucide-react';
import { LngLatBounds, MapEvent } from 'mapbox-gl';
import { useCallback, useEffect, useMemo } from 'react';
import {
  Map,
  NavigationControl,
  Popup,
  ScaleControl,
  ViewStateChangeEvent,
} from 'react-map-gl';
import { TextMarker } from '~/apps/consumer/components/Map/MapMarkers/TextMarker';
import { DEFAULT_POSITION } from '~/apps/consumer/components/MapSearch/constants';
import { useExploreContext } from '~/apps/consumer/components/MapSearch/ExploreContext';
import { ExploreMapMarker } from '~/apps/consumer/components/MapSearch/ExploreMap/ExploreMapMarker';
import { convertBoundsToBbox } from '~/apps/consumer/components/MapSearch/helpers';
import { ComparableState } from '~/apps/consumer/pages/ListingSetup/Comparables/ComparablesPage';
import { getListingPrice } from '~/apps/consumer/utils/listing.utils';
import { useIsMobile } from '~/common/hooks/useIsMobile';
import { getInitialCompsBBox } from '../../../BuyerOfferAnalysis/Comps/utils';
import { NormalizedPropertyDetails } from '../../../Listings/ListingPage/utils';
import { EditCompsListingCard } from '../EditCompsListingCard';
import { EditCompsMapSidebarListingCard } from './EditCompsMapSidebar/ListingCard/EditCompsMapSidebarListingCard';

export const EditCompsMap = ({
  mainListing,
  comparableSet,
  comps,
  setComps,
  initialSelectedComp,
  setInitialSelectedComp,
}: {
  mainListing: NormalizedPropertyDetails;
  comparableSet?: ListingComparableSetFragment;
  comps: ComparableState[];
  setComps: (comps: ComparableState[]) => void;
  initialSelectedComp?: ComparableState;
  setInitialSelectedComp?: (comp?: ComparableState | undefined) => void;
}) => {
  const isMobile = useIsMobile();

  const {
    listings,
    updateFilters,
    selectedListing,
    setSelectedListing,
    loading,
    mapRef,
  } = useExploreContext();

  useEffect(() => {
    if (initialSelectedComp && !loading && listings?.length > 0) {
      setSelectedListing(initialSelectedComp);
      if (setInitialSelectedComp) {
        setInitialSelectedComp(undefined);
      }
    }
  }, [
    initialSelectedComp,
    setSelectedListing,
    setInitialSelectedComp,
    loading,
    listings,
  ]);

  useEffect(() => {
    if (selectedListing?.mlsListing?.id === mainListing.mlsListingId) {
      setSelectedListing(null);
    }
  }, [selectedListing, setSelectedListing, mainListing]);

  const initialBBox = getInitialCompsBBox(comparableSet, mainListing);
  const initialBounds = initialBBox
    ? new LngLatBounds([
        initialBBox[0],
        initialBBox[1],
        initialBBox[2],
        initialBBox[3],
      ])
    : undefined;

  const fetchListings = useCallback(
    (event: MapEvent) => {
      const bounds = event.target.getBounds();
      const bbox = convertBoundsToBbox(bounds);

      if (bbox) {
        updateFilters({
          bbox,
        });
      }
    },
    [updateFilters]
  );

  const handleMove = useMemo(
    () =>
      debounce((event: ViewStateChangeEvent) => {
        fetchListings(event);
      }, 300),
    [fetchListings]
  );

  const markers = useMemo(() => {
    const mainMarker = mainListing?.location ? (
      <ExploreMapMarker
        key={`marker-${mainListing.id}-main`}
        lat={mainListing.location.lat}
        lng={mainListing.location.lon}
        markerOverride={
          <TextMarker
            text={
              <Flex alignItems="center" gap={1}>
                <Icon as={StarIcon} />
                This home
              </Flex>
            }
            zIndex={100}
          />
        }
        zIndex={2}
        zoom={mapRef.current?.getZoom() || DEFAULT_POSITION.zoom}
      />
    ) : null;

    const comparableMarkers = comps?.map(({ mlsListing }) => {
      if (!mlsListing.geo?.location?.lat || !mlsListing.geo?.location?.lon) {
        return null;
      }

      const price = getListingPrice(mlsListing);

      return (
        <ExploreMapMarker
          key={`marker-${mlsListing.id}-comp`}
          isPremium={true}
          lat={mlsListing.geo.location.lat}
          lng={mlsListing.geo.location.lon}
          price={price}
          zoom={mapRef.current?.getZoom() || DEFAULT_POSITION.zoom}
          onClick={(event) => {
            event.originalEvent.stopPropagation();
            setSelectedListing({
              mlsListing: mlsListing,
            });
          }}
        />
      );
    });

    const otherMarkers = listings?.map(({ listing, mlsListing }) => {
      if (!mlsListing.geo?.location?.lat || !mlsListing.geo?.location?.lon) {
        return null;
      }

      // filter out other markers if there is already a comp
      if (comps?.some(({ mlsListing: comp }) => comp.id === mlsListing.id)) {
        return null;
      }

      const price = getListingPrice(mlsListing);

      return (
        <ExploreMapMarker
          key={`marker-${mlsListing.id}-other`}
          isPremium={false}
          lat={mlsListing.geo.location.lat}
          lng={mlsListing.geo.location.lon}
          price={price}
          zoom={mapRef.current?.getZoom() || DEFAULT_POSITION.zoom}
          onClick={(event) => {
            event.originalEvent.stopPropagation();
            setSelectedListing({
              listing,
              mlsListing,
            });
          }}
        />
      );
    });

    return [...(comparableMarkers ?? []), mainMarker, ...otherMarkers];
  }, [mainListing, mapRef, listings, setSelectedListing, comps]);

  const isSelectedListingAdded = comparableSet?.listingComparables?.some(
    (comparable) => comparable.mlsListing.id === selectedListing?.mlsListing?.id
  );

  const handleAddRemove = () => {
    if (!selectedListing?.mlsListing) {
      return;
    }

    let newComps = [] as ComparableState[];
    if (isSelectedListingAdded) {
      newComps = comps?.filter(
        (comp) => comp.mlsListing.id !== selectedListing?.mlsListing?.id
      );
    } else {
      newComps = [
        ...comps,
        {
          mlsListing: selectedListing?.mlsListing,
        },
      ];
    }

    setComps(newComps);
  };

  return (
    <Flex
      direction={{ base: 'column-reverse', xl: 'column' }}
      flexBasis={0}
      flexGrow={1}
      height="100%"
    >
      <Map
        ref={mapRef}
        initialViewState={{
          bounds: initialBounds,
          longitude: mainListing?.location?.lon,
          latitude: mainListing?.location?.lat,
          zoom: 12,
        }}
        mapStyle="mapbox://styles/mapbox/navigation-guidance-night-v4"
        mapboxAccessToken={window.ENVS.mapboxApiKey}
        minZoom={3}
        padding={
          isMobile
            ? { top: 70, right: 0, bottom: 0, left: 0 }
            : { top: 50, right: 300, bottom: 0, left: 0 }
        }
        scrollZoom={false}
        style={{ borderRadius: isMobile ? '0' : '8px' }}
        testMode={window.ENVS.environment === 'e2e'}
        onLoad={fetchListings}
        onMove={handleMove}
      >
        <NavigationControl position="bottom-left" />
        <ScaleControl />
        {markers}
        {selectedListing?.mlsListing && (
          <Popup
            latitude={selectedListing.mlsListing.geo.location.lat}
            longitude={selectedListing.mlsListing.geo.location.lon}
            offset={15}
            style={{
              maxWidth: '260px',
              minWidth: '260px',
              zIndex: 2,
              borderRadius: '12px',
            }}
            onClose={() => setSelectedListing(null)}
          >
            {isMobile ? (
              <EditCompsMapSidebarListingCard
                bottomButton={
                  <Button size="xs" width="100%" onClick={handleAddRemove}>
                    {isSelectedListingAdded ? 'Remove Comp' : 'Add Comp'}
                  </Button>
                }
                isHoverable={false}
                mlsListing={selectedListing.mlsListing}
              />
            ) : (
              <EditCompsListingCard
                isAdded={comparableSet?.listingComparables?.some(
                  (comparable) =>
                    comparable.mlsListing.id === selectedListing.mlsListing.id
                )}
                mlsListing={selectedListing.mlsListing}
                onClick={handleAddRemove}
              />
            )}
          </Popup>
        )}
      </Map>
    </Flex>
  );
};
